import { Formik } from 'formik';
import { GraphQLError } from 'graphql';
import pick from 'lodash/pick';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useSearchParam } from 'react-use';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useWorkspace, useWorkspaceConfiguration } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { MfaMethod } from '@treams/treams-network';
import {
	ArrowRightIcon,
	Button,
	Callout,
	FlexGap,
	GraphQLErrorMessage,
	Input,
	Label,
	Paragraph,
	Separator,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import WorkspaceNotFound from '../../../../../../src/onboarding/WorkspaceNotFound';
import BareLayout from '../../../../../../src/packages/kakapo-ui-application/layouts/BareLayout';
import PublicForm from '../../components/authentication/PublicForm';
import { appDomain, workspaceAuthenticationUrl } from './appDomain';
import NewPasswordRequiredChallenge from './challenges/NewPasswordRequiredChallenge';
import PasswordChallenge from './challenges/PasswordChallenge';
import TotpMfaChallenge from './challenges/TotpMfaChallenge';
import TotpMfaSetupChallenge from './challenges/TotpMfaSetupChallenge';

const SsoButton = styled(Button)`
	&& {
		width: 100%;
		min-width: 0;
	}
`;

const renderChallenge = (
	login: string,
	challenge: graphql.Challenge,
	onCancel: () => void,
	onResponse: (response: graphql.UpdateSessionResponse) => void,
	error?: GraphQLError,
	isPrefill?: boolean,
) => {
	if (challenge.password) {
		return (
			<PasswordChallenge
				login={login}
				error={error}
				context={challenge.context}
				challenge={challenge.password}
				onCancel={onCancel}
				onResponse={onResponse}
				isPrefill={isPrefill}
			/>
		);
	}

	if (challenge.newPasswordRequired) {
		return (
			<NewPasswordRequiredChallenge
				login={login}
				context={challenge.context}
				challenge={challenge.newPasswordRequired}
				onResponse={onResponse}
				isPrefill={isPrefill}
			/>
		);
	}

	if (challenge.singleSignOn) {
		window.location.href = challenge.singleSignOn.url;
	}

	if (challenge.mfaSetup) {
		if (!challenge.mfaSetup.allowedMethods.includes(MfaMethod.Totp)) {
			throw new Error(
				'Cannot render MFA setup challenge for types: ' +
					challenge.mfaSetup.allowedMethods.join(', '),
			);
		}

		return (
			<TotpMfaSetupChallenge
				login={login}
				context={challenge.context}
				challenge={challenge.mfaSetup}
				onResponse={onResponse}
				onCancel={onCancel}
			/>
		);
	}

	if (challenge.totpMfa) {
		return (
			<TotpMfaChallenge
				context={challenge.context}
				challenge={challenge.totpMfa}
				onResponse={onResponse}
				onCancel={onCancel}
			/>
		);
	}

	return null;
};

type PasswordChallengeResponse = graphql.UpdateSessionResponse | graphql.CreateSessionResponse;

export default function Login() {
	const workspace = useWorkspace();
	const { t } = useTranslation();
	const history = useHistory();
	const redirect = useSearchParam('redirect');
	const location = useLocation<Record<string, string>>();
	const { data: configuration, loading } = useWorkspaceConfiguration();

	let home = '/';

	try {
		home = sessionStorage.getItem('original-path') || '/';
	} catch {
		// sessionStorage can throw in private mode or when storage is disabled
	}

	const [commit] = graphql.useLoginMutation();
	const [verify] = graphql.useVerifySessionMutation();
	const [refresh] = graphql.useRefreshSessionMutation();
	const [exchange] = graphql.useExchangeSessionMutation();

	const [login, setLogin] = useState('');
	const [challenge, setChallenge] = useState<graphql.Challenge | undefined>(undefined);
	const [error, setError] = useState<GraphQLError | undefined>(undefined);

	const onCancel = () => {
		setChallenge(undefined);
		setError(undefined);
	};

	const onResponse = (response: PasswordChallengeResponse) => {
		if (response.challenge) {
			setChallenge(response.challenge);

			return response.challenge;
		}

		if ('session' in response && response.session) {
			// The mutation set the HttpOnly session cookie; the full-page
			// navigation below bootstraps the app from the server-side slots.
			clearOriginalPath();

			window.location.href = resolveLoginTarget(
				redirect ?? location.state?.originalTargetUrl ?? home,
			);
		}
	};

	useEffect(
		() =>
			void (async () => {
				if (!window.location.hash) {
					if (!configuration?.redirect) return;

					const currentUrl = window.location.href;
					const isRedirected = currentUrl.includes(configuration.redirect);

					// Only redirect if we are not currently on the website we should redirect to
					if (!isRedirected) {
						window.location.href = configuration.redirect;
					}

					return;
				}

				const hashParams = new URLSearchParams(window.location.hash.slice(1));
				const ssoResponse = {
					accessToken: hashParams.get('access_token'),
					refreshToken: hashParams.get('refresh_token'),
					state: hashParams.get('state'),
					errorDescription: hashParams.get('error_description'),
				};

				const domain = appDomain(window.location.hostname);

				if (workspace.id === 'support' && ssoResponse.state && domain) {
					const url = workspaceAuthenticationUrl(ssoResponse.state, domain, window.location.hash);

					if (url) {
						window.location.href = url;
					}

					return;
				}

				if (ssoResponse.errorDescription) {
					if (ssoResponse.errorDescription.includes('Invalid ProviderName/Username combination')) {
						history.push('/invalid-nameid');
						return;
					}
				}

				try {
					await verify({
						variables: {
							input: {
								accessToken: ssoResponse.accessToken as string,
							},
						},
					});

					// Immediately refresh the session to persist a browser session
					// (sets the HttpOnly session cookie).
					await refresh({
						variables: {
							input: {
								refreshToken: ssoResponse.refreshToken as string,
							},
						},
					});

					clearOriginalPath();

					window.location.href = resolveLoginTarget(home);
				} catch (error) {
					const { key } = error.graphQLErrors[0].extensions.error;

					if (key === 'authorization.errors.unregisteredAccount') {
						history.push('/unregistered-account');
					}
				}
			})(),
		[window.location.hash, configuration?.redirect],
	);

	useEffect(
		() =>
			void (async () => {
				const searchParams = new URLSearchParams(window.location.search.slice(1));
				const ssoResponse = {
					code: searchParams.get('code'),
					state: searchParams.get('state'),
					errorDescription: searchParams.get('error_description'),
				};

				if (ssoResponse.errorDescription) {
					if (ssoResponse.errorDescription.includes('Invalid ProviderName/Username combination')) {
						history.push('/invalid-nameid');
						return;
					}
				}

				if (!ssoResponse.code || !ssoResponse.state) {
					return;
				}

				const domain = appDomain(window.location.hostname);

				if (workspace.id === 'support' && ssoResponse.state && domain) {
					const url = workspaceAuthenticationUrl(ssoResponse.state, domain, window.location.search);

					if (url) {
						window.location.href = url;
					}

					return;
				}

				const state = JSON.parse(ssoResponse.state);

				try {
					// The mutation sets the HttpOnly session cookie and persists the
					// authorizing workspace on the server-side slot for refreshes.
					await exchange({
						variables: {
							input: {
								state: pick(state, ['workspaceID', 'authorizingWorkspaceID']),
								authorizationCode: ssoResponse.code as string,
							},
						},
					});

					clearOriginalPath();

					window.location.href = resolveLoginTarget(home);
				} catch (error) {
					const { key } = error.graphQLErrors[0].extensions.error;

					if (key === 'authorization.errors.unregisteredAccount') {
						history.push('/unregistered-account');
					}
				}
			})(),
		[window.location.search],
	);

	const prefillSearchParams = new URLSearchParams(window.location.search.slice(1));
	const prefill = {
		username: prefillSearchParams.get('username'),
		code: prefillSearchParams.get('code'),
	};
	const onSubmit = async ({ login: value }: { login: string }) => {
		setLogin(value.trim());

		try {
			const { data } = await commit({
				variables: {
					input: {
						login: value.trim(),
					},
				},
			});

			return onResponse(data!.createSession);
		} catch (errors) {
			setError(errors.graphQLErrors[0]);
		}
	};

	const [commitPassword] = graphql.usePasswordChallengeMutation();
	useEffect(() => {
		const { username } = prefill;
		if (!username) {
			return;
		}

		(async () => {
			const challenge = await onSubmit({ login: username });

			if (!challenge || !prefill.code) {
				return;
			}

			try {
				const { data } = await commitPassword({
					variables: {
						input: {
							response: {
								context: challenge.context,
								password: {
									username: username,
									password: prefill.code,
								},
							},
						},
					},
				});

				onResponse(data!.updateSession);
			} catch (errors) {
				setError(errors.graphQLErrors[0]);
			}
		})();
	}, [prefill.username]);

	if (!loading && !configuration) {
		return <WorkspaceNotFound />;
	}

	const isAlreadyOnRedirect =
		configuration?.redirect && window.location.href.includes(configuration.redirect);
	const shouldShowRedirectSpinner =
		!window.location.hash && configuration?.redirect && !isAlreadyOnRedirect;

	return (
		<BareLayout>
			{challenge ? (
				renderChallenge(login, challenge, onCancel, onResponse, error, !!prefill.username)
			) : (
				<Formik
					initialValues={{
						login: prefill.username ?? login,
					}}
					onSubmit={onSubmit}
				>
					{({ values }) => (
						<PublicForm>
							{loading || shouldShowRedirectSpinner ? (
								<Flex justifyContent="center">
									<Spinner />
								</Flex>
							) : (
								<>
									<GraphQLErrorMessage error={error} />

									{configuration?.singleSignOns?.length === 1 && (
										<FlexGap
											flexDirection="column"
											gap={tokens.gap.group}
											style={{ marginBottom: tokens.gap.section }}
										>
											<Callout color="blue">{t('auth.login.sso.explanation')}</Callout>

											<SsoButton title={t('auth.login.sso.button')} link="/sso" />

											<Separator.Inlay>
												<Paragraph small>{t('auth.login.sso.alternative')}</Paragraph>
											</Separator.Inlay>
										</FlexGap>
									)}

									<Box mb={3}>
										<Label mb={2}>{t('common.userNameOrEmail')}:</Label>
										<Input.Formik name="login" autoFocus />
									</Box>

									<Button.Formik
										title={t('common.next')}
										rightIcon={<ArrowRightIcon />}
										disabled={values.login === ''}
										disableSuccess={Boolean(error)}
									/>
								</>
							)}
						</PublicForm>
					)}
				</Formik>
			)}
		</BareLayout>
	);
}

/**
 * Never land on /logout after login — instant sign-out loop.
 * @param target The candidate post-login path.
 */
function resolveLoginTarget(target: string): string {
	return target.startsWith('/logout') ? '/' : target;
}

function clearOriginalPath() {
	try {
		sessionStorage.removeItem('original-path');
	} catch {
		// sessionStorage can throw in private mode or when storage is disabled
	}
}
